import React from 'react';
import { formatDate, formatInteger } from '@oms/utils';
import { formatNumberWithAbbr } from './util';
import { CompanyInfo as OMSCompanyInfo } from '@oms/components-company-info';
import { I18n } from '@lingui/react';
import { Trans } from '@lingui/macro';
import { ModuleSection, ModuleHeading, ModuleProps } from 'components/Module';
import getExchangeName from 'utils/getExchangeName';
import mapSI from 'utils/mapSI';
import mapICB from 'utils/mapICB';
import { useCustomerByToken } from 'client/customers';
import { Table, Tbody, Td, Th, Tr } from '@oms/ui-table';
import { useLanguage } from '@oms/ui-language-context';

export function isNumber(value: unknown) {
  return typeof value === 'number';
}

function getDateFormat(language: string) {
  const dateFormats: Record<string, string> = {
    en: `DD-MM-YYYY`,
    no: 'DD.MM.YYYY',
  };
  const dateFormat = dateFormats[language] || dateFormats.en;
  return dateFormat;
}

export default function CompanyInfo({
  token,
  standalone = true,
  ...styleProps
}: ModuleProps) {
  const data = useCustomerByToken(token);
  const lang = useLanguage();
  return (
    <ModuleSection {...styleProps}>
      {standalone ? null : (
        <ModuleHeading>
          <Trans>Company info</Trans>
        </ModuleHeading>
      )}
      <OMSCompanyInfo
        itemSector={undefined as any}
        spec={{ product: 'companyInfo' }}
        rightSide={({
          // FACE_VALUE,
          LISTING_DATE = data?.notingdate,
          ISSUED_NUMBER_OF_SHARES,
          MARKET_CAP,
          SHARE_CAPITAL,
          SHARE_CAPITAL_CURRENCY_CODE,
        }: {
          LISTING_DATE: any;
          ISSUED_NUMBER_OF_SHARES: number;
          MARKET_CAP: number;
          OUTSTANDING_NUMBER_OF_SHARES: number;
          SHARE_CAPITAL: number;
          SHARE_CAPITAL_CURRENCY_CODE: string;
        }) => {
          return (
            <Table variant="keyValue">
              <Tbody>
                <Tr>
                  <Th>
                    <Trans>Listed</Trans>
                  </Th>
                  <Td>
                    <I18n>
                      {({ i18n }) => {
                        const language = i18n.language || 'en';
                        const dateFormat = getDateFormat(language);
                        return LISTING_DATE
                          ? formatDate(LISTING_DATE, dateFormat)
                          : data?.notingdate
                          ? formatDate(data?.notingdate, dateFormat)
                          : '-';
                      }}
                    </I18n>
                  </Td>
                </Tr>
                <Tr>
                  <Th>
                    <Trans>No. of shares issued</Trans>
                  </Th>
                  <Td>
                    {isNumber(ISSUED_NUMBER_OF_SHARES)
                      ? formatInteger(ISSUED_NUMBER_OF_SHARES)
                      : '-'}
                  </Td>
                </Tr>
                <Tr>
                  <Th>
                    <Trans>Market cap.</Trans>
                  </Th>
                  <Td>
                    {isNumber(MARKET_CAP)
                      ? formatNumberWithAbbr(MARKET_CAP, lang)
                      : '-'}
                  </Td>
                </Tr>
                <Tr>
                  <Th>
                    <Trans>Share capital</Trans>
                  </Th>
                  <Td>
                    {isNumber(SHARE_CAPITAL)
                      ? `${formatNumberWithAbbr(SHARE_CAPITAL, lang)} ${
                          SHARE_CAPITAL_CURRENCY_CODE
                            ? SHARE_CAPITAL_CURRENCY_CODE
                            : ''
                        }`
                      : '-'}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          );
        }}
        leftSide={({
          // GICS_CODE,
          ITEM,
          ORGANISATION_NUMBER,
          ISIN,
          SECTOR,
          ICB_CODE,
          SECTOR_INDEX_MEMBER_OF,
        }: {
          ITEM: string;
          ORGANISATION_NUMBER: string;
          ISIN: string;
          SECTOR: string;
          GICS_CODE: string;
          ICB_CODE: string;
          SECTOR_INDEX_MEMBER_OF: string;
        }) => {
          return (
            <Table variant="keyValue">
              <Tbody>
                <Tr>
                  <Th>
                    <Trans>Ticker</Trans>
                  </Th>
                  <Td>{ITEM || '-'}</Td>
                </Tr>
                {ORGANISATION_NUMBER ? (
                  <Tr>
                    <Th>
                      <Trans>Organization number</Trans>
                    </Th>
                    <Td>{ORGANISATION_NUMBER || ''}</Td>
                  </Tr>
                ) : null}
                <Tr>
                  <Th>
                    <Trans>ISIN</Trans>
                  </Th>
                  <Td>{ISIN || '-'}</Td>
                </Tr>
                <Tr>
                  <Th>
                    <Trans>Exchange</Trans>
                  </Th>
                  <Td>{getExchangeName(SECTOR) || '-'}</Td>
                </Tr>
                <Tr>
                  <Th>
                    <Trans>Sector</Trans>
                  </Th>
                  <Td>
                    {mapSI(SECTOR_INDEX_MEMBER_OF) ||
                      mapICB(ICB_CODE) ||
                      data?.sector || <Trans>Other</Trans>}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          );
        }}
      />
    </ModuleSection>
  );
}

export { CompanyInfo };
